import React, {useEffect, useRef, useState} from "react";
import {Button} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheck, faExclamationCircle} from "@fortawesome/free-solid-svg-icons";
import ModalControls from "../../common/ModalControls";
import {useNavigate, useLocation} from "react-router-dom";
import {defaultFormState} from "../../../../defaults/Forms";
import {documentNumberingPatternDefault} from "../../../../defaults/Items";
import {
  onSaveAndClose,
  compareData,
  restoreFormData,
  onItemsListDeleteButtonClick,
  onCloseDetails,
  setForegroundColor,
  validateNumberField,
  validateTextField,
  validateSelectField,
  resetInvalidInputField
} from "../../../../helpers/ComponentHelper";

import AppComponentCardHeader from "../../common/AppComponentCardHeader";
import LoadingDataDiv from "../../common/LoadingDataDiv";
import DeleteWarningModal from "../../common/DeleteWarningModal";
import SettingsEntityModal from "../../common/SettingsEntityModal";
import TextInput from "../../../elements/TextInput";
import TextArea from "../../../elements/TextArea";
import useCrud from "../../../../hooks/useCrud";
import {Table} from "../../../table/Table";
import {faInfoCircle} from "@fortawesome/free-solid-svg-icons";
import Select from "../../../elements/Select";
import OptionsList from "../../../elements/OptionsList";

const DocumentNumberingPattern = () => {

  const dataUrl = "/document-numbering-patterns";
  const contractTypesUrl = "/contract-types";
  const relatedItemsUrl = null;
  const defaultItem = documentNumberingPatternDefault;
  const itemName = "wzorzec numeracji";
  const itemNames = "wzorce numeracji";
  const navigate = useNavigate();
  const location = useLocation();
  const {createItem, updateItem, deleteItem, getItems, getRelatedChildrenByParentId} = useCrud(dataUrl);

  const [loading, setLoading] = useState(true);
  const [allowDelete, setAllowDelete] = useState(null);
  const [currentFormState, setCurrentFormState] = useState(defaultFormState);
  const [itemsList, setItems] = useState([]);
  const [currentItem, setCurrentItem] = useState(defaultItem);
  const [backupItem, setBackupItem] = useState(defaultItem);
  const [itemChanged, setItemChanged] = useState(false);
  const [updateConflict, setUpdateConflict] = useState(false);
  const [contractTypesList, setContractTypesList] = useState([]);
  const [validPattern, setValidPattern] = useState(false);
  const [pattern, setPattern] = useState('');
  const [patternExample, setPatternExample] = useState('');
  const [symbol, setSymbol] = useState('');
  const [yearFormat, setYearFormat] = useState('');
  const [monthFormat, setMonthFormat] = useState('');

  const patternRef = useRef();
  // PATTERN WITH REQUIRED MONTH AND YEAR
  // const NUMBERING_PATTERN_REGEX = /^(?!.*([RMN]).*\1)(?=[^S]*S?[^S]*$)(?=.*R)(?=.*M)(?=.*N)[RMNS \/:\-_\\]*$/
  const NUMBERING_PATTERN_REGEX = /^(?!.*([RMN]).*\1)(?=[^S]*S?[^S]*$)(?=.*N)[RMNS \/:\-_\\]*$/

  // elements related to current item
  const columns = [
    {label: "Id", accessor: "id", sortable: true, searchable: false},
    {label: "Rok", accessor: "year", sortable: true, searchable: true},
    {label: "Wzorzec", accessor: "pattern", sortable: true, searchable: true},
    {label: "Tryb", accessor: "continuousNumbering", sortable: true, searchable: false, format: value => value ? "ciągła w roku" : "miesięczna"},
    {label: "Użyty", accessor: "locked", sortable: true, searchable: false, type: "bool"},
    {
      label: "Typ umowy",
      accessor: "contractType",
      sortable: true,
      searchable: true,
      format: (contractType) => contractType ? `${contractType.name}` : "",
      fieldsToInclude: ['name']
    },
    {label: "Edycja", accessor: "editBtn", sortable: false, searchable: false},
    {label: "Usuń", accessor: "deleteBtn", sortable: false, searchable: false},
  ];


  const state = {
    itemsList, setItems,
    currentItem, setCurrentItem,
    currentFormState, setCurrentFormState,
    defaultItem, backupItem, setBackupItem, itemChanged, setItemChanged,
    setAllowDelete,
    setRelatedItems: null
  }


  const onDelete = async () => {

    const response = await deleteItem(`${dataUrl}/${currentItem.id}`, currentItem.id, state);
    if (response === 401 || response === 403) {
      navigate('/login', {state: {from: location}, replace: true});
    }
  }


  const onSaveItemClick = async (e) => {
    e.preventDefault();

    const formIsValid = fieldsValidation.every(isValid => isValid);
    if (!formIsValid) {
      return;
    }

    let response;
    const item = {
      id: currentItem.id,
      year: currentItem.year,
      symbol: currentItem.symbol,
      yearFormat: currentItem.yearFormat,
      monthFormat: currentItem.monthFormat,
      pattern: currentItem.pattern,
      continuousNumbering: !!currentItem.continuousNumbering,
      contractType: currentItem.contractType && currentItem.contractType.id > 0 ? {id: currentItem.contractType.id} : null
    };


    if (currentFormState.formAddingDataMode) {
      response = await createItem(dataUrl, item, state);
    } else {
      response = await updateItem(`${dataUrl}/${item.id}`, item, state);
    }

    if (response.status === 401 || response.status === 403) {
      navigate('/login', {state: {from: location}, replace: true});
    } else if (response.status === 409) {
      setUpdateConflict(true);
    } else {
      response && onSaveAndClose({state});
    }
  }

  const checkRelatedItems = async (id) => {
    setAllowDelete(true);
  }


  const monthlyModeIsValid = !!currentItem.continuousNumbering || (currentItem.pattern || "").includes("M");

  const fieldsValidation = [
    validateNumberField(currentItem.year, "year", 2000, 2999),
    validateTextField(currentItem.pattern, "pattern"),
    validateSelectField(currentItem.contractType),
    validPattern,
    monthlyModeIsValid
  ];


  const onClose = () => {
    setUpdateConflict(false);
    onCloseDetails({state});
  };


  useEffect(() => {
    if (updateConflict) setUpdateConflict(false);
    compareData(currentFormState, setCurrentFormState, currentItem, backupItem);
  }, [itemChanged])


  useEffect(() => {
    if (allowDelete !== null) {
      onItemsListDeleteButtonClick(currentFormState, setCurrentFormState, itemName, allowDelete, currentItem.name);
    }
  }, [allowDelete])


  useEffect(() => {
    const getData = async () => {
      const [itemsData, contractTypesData] =
        await Promise.all([
          getItems(dataUrl),
          getItems(contractTypesUrl)
        ]);
      const success =
        itemsData.status === 200 &&
        contractTypesData.status === 200
      if (success) {
        setLoading(false);
        setItems(itemsData.data);
        setContractTypesList(contractTypesData.data);
        setPattern('');
      } else {
        alert("Nie udało się pobrać danych.");
      }
    };
    getData();
  }, [])


  useEffect(() => {
    const isValid = NUMBERING_PATTERN_REGEX.test(pattern);
    setValidPattern(isValid);

    if (isValid) {
      generatePatternExample();
    } else {
      setPatternExample("");
    }
  }, [pattern, symbol, yearFormat, monthFormat, currentItem.year]);

  useEffect(() => {
    setPattern(currentItem.pattern || "");
    setSymbol(currentItem.symbol || "");
    setYearFormat(currentItem.yearFormat || "rrrr");
    setMonthFormat(currentItem.monthFormat || "mm");
  }, [currentItem.pattern, currentItem.symbol, currentItem.yearFormat, currentItem.monthFormat]);


  const getCompleteItem = async (id) => {
    const response = await getItems(`${dataUrl}/${id}`);
    if (response.status === 200) {
      setPattern(response.data.pattern || "");
      setSymbol(response.data.symbol || "");
      setYearFormat(response.data.yearFormat || "rrrr");
      setMonthFormat(response.data.monthFormat || "mm");
      return response.data;
    } else if (response.status === 401 || response.status === 403) {
      navigate('/login', {state: {from: location}, replace: true})
    } else {
      alert("Nie udało się pobrać danych.");
    }
  }


  useEffect(() => {
    if (currentFormState.showForm && currentFormState.formAddingDataMode) {
      setSymbol(defaultItem.symbol || currentItem.symbol);
      setYearFormat(defaultItem.yearFormat || currentItem.yearFormat);
      setMonthFormat(defaultItem.monthFormat || currentItem.monthFormat);
      setPattern(defaultItem.pattern || currentItem.pattern);
    }
  }, [currentFormState.showForm, currentFormState.formAddingDataMode]);


  const generatePatternExample = () => {

    const months = {
      1: 'STY',
      2: 'LUT',
      3: 'MAR',
      4: 'KWI',
      5: 'MAJ',
      6: 'CZE',
      7: 'LIP',
      8: 'SIE',
      9: 'WRZ',
      10: 'PAŹ',
      11: 'LIS',
      12: 'GRU'
    };

    const currentYear = String(currentItem.year || new Date().getFullYear());
    const currentMonth = new Date().getMonth() + 1;
    const exampleNumber = 123;

    const formattedYear = yearFormat === 'rr' ? currentYear.slice(-2) : currentYear;
    const formattedMonth = monthFormat === 'mmm'
      ? months[currentMonth]
      : monthFormat === 'mm'
        ? currentMonth.toString().padStart(2, '0')
        : currentMonth.toString();

    const example = [...pattern].map(token => {
      if (token === 'S') return symbol || '';
      if (token === 'R') return formattedYear;
      if (token === 'M') return formattedMonth;
      if (token === 'N') return exampleNumber.toString();
      return token;
    }).join('');

    setPatternExample(example);
  };


  const addDataButtonProps = {
    setCurrentItem,
    setBackupItem,
    defaultItem,
    currentFormState,
    setCurrentFormState,
    formDescription: null,
    formHeader: `Nowy element`,
    buttonTitle: `Dodaj wzorzec numeracji umów`
  }


  let dataSectionContent;
  if (loading) {
    dataSectionContent = <LoadingDataDiv/>
  } else if (itemsList.length > 0) {
    dataSectionContent =
      <Table
        rows={itemsList}
        columns={columns}
        state={state}
        checkRelatedItems={checkRelatedItems}
        formHeader={`Edycja ${itemName}`}
        relatedItemsUrl={relatedItemsUrl}
        getCompleteItem={getCompleteItem}
      />
  } else {
    dataSectionContent =
      <div className={"warn_info"}><FontAwesomeIcon
        className={"warning-icon div-visible"} icon={faExclamationCircle}/>&nbsp;&nbsp;&nbsp;NIE ZNALEZIONO ŻADNYCH
        DANYCH
      </div>
  }
  return (
    <div id="layoutSidenav_content" className="settings-entity-page">
      <AppComponentCardHeader title="Wzorce numeracji umów" buttonProps={addDataButtonProps}/>

      <div className="container-fluid px-4 py-3">
        <div className={itemsList.length > 0 ? "card shadow p-1 mb-5 bg-white rounded-2" : "card shadow mb-5 bg-transparent rounded-2"}>
          {dataSectionContent}
        </div>
      </div>

      <SettingsEntityModal
        show={currentFormState.showForm}
        onClose={onClose}
        title={currentFormState.formAddingDataMode ? "Nowa pozycja: Wzorzec numeracji" : `Edycja: ${currentItem.name || "Wzorzec numeracji"}`}
        adding={currentFormState.formAddingDataMode}
        introText="Wzorzec określa sposób automatycznego nadawania numerów umowom dla wybranego roku i typu umowy. Treść dokumentu jest definiowana osobno w Szablonach treści umów."
        onRestore={() => { setUpdateConflict(false); restoreFormData({state}); }}
        onDelete={onDelete}
        onSubmit={onSaveItemClick}
        state={state}
        size="xl"
      >
        <form id="add-document-numbering-pattern-form" name="add-document-numbering-pattern-form" className="settings-form-content-card">

                            {/*<div className="row form-group mb-4">*/}
                            {/*  <div className="col-md-3 form-label">*/}
                            {/*    <label htmlFor="contractTypes" className="">Typ umowy *</label>*/}
                            {/*  </div>*/}
                            {/*  <div className="col-md-9">*/}
                            {/*    <OptionsList*/}
                            {/*      label="Typy umów *"*/}
                            {/*      propertyName="contractTypeId"*/}
                            {/*      required={false}*/}
                            {/*      state={state}*/}
                            {/*      itemsList={contractTypesList}*/}
                            {/*      itemName="contractTypes"*/}
                            {/*      displayNames={["name"]}*/}
                            {/*    />*/}
                            {/*  </div>*/}
                            {/*</div>*/}

                            <div className="row form-group mb-4">
                              <div className="col-md-3 form-label">
                                <label htmlFor="contractType" className="">Typ umowy *</label>
                              </div>
                              <div className="col-md-9">
                                <Select
                                  label="contractType"
                                  propertyName="contractTypeId"
                                  required={false}
                                  state={state}
                                  itemsList={contractTypesList}
                                  itemName="contractType"
                                  displayNames={["name"]}
                                  disabled={!currentFormState.formAddingDataMode && currentItem.locked}
                                />
                              </div>
                            </div>

                            <div className="row form-group mb-4">
                              <div className="col-md-3 form-label">
                                <label htmlFor="year" className="">Rok *</label>
                              </div>
                              <div className="col-md-9">
                                <TextInput propertyName="year" required={true} state={state} disabled={!currentFormState.formAddingDataMode && currentItem.locked}/>
                              </div>
                            </div>

                            <div className="row form-group mb-4">
                              <div className="col-md-3 form-label">
                                <label htmlFor="pattern" className="">Wzorzec *</label>
                              </div>


                              <div className="row col-md-9">

                                <div className="col-md-2">
                                  <label htmlFor="symbol" className="">Symbol</label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    id="symbol"
                                    autoComplete="off"
                                    value={currentItem.symbol}
                                    disabled={!currentFormState.formAddingDataMode && currentItem.locked}
                                    required
                                    onChange={(e) => {
                                      setSymbol(e.target.value);
                                      setItemChanged(!itemChanged);
                                      setCurrentItem({
                                        ...currentItem,
                                        symbol: e.target.value
                                      });
                                      setCurrentFormState({...currentFormState, formSaveButtonDisabled: false});
                                    }}
                                    onClick={() => resetInvalidInputField("symbol")}
                                  />
                                </div>

                                <div className="col-md-2">
                                  <label htmlFor="yearFormat" className="">Format roku</label>
                                  <div>
                                    <select name="yearFormat"
                                            value={currentItem?.yearFormat || ""}
                                            disabled={!currentFormState.formAddingDataMode && currentItem.locked}
                                            className="form-select"
                                            onChange={(e) => {
                                              setYearFormat(e.target.value);
                                              setItemChanged(!itemChanged);
                                              setCurrentItem({
                                                ...currentItem,
                                                yearFormat: e.target.value
                                              });
                                            }
                                            }>
                                      <option value="rrrr">RRRR (2025)</option>
                                      <option value="rr">RR (25)</option>
                                    </select>
                                  </div>
                                </div>

                                <div className="col-md-2">
                                  <label htmlFor="monthFormat" className="">Format miesiąca</label>
                                  <div>
                                    <select name="monthFormat"
                                            value={currentItem?.monthFormat || ""}
                                            disabled={!currentFormState.formAddingDataMode && currentItem.locked}
                                            className="form-select"
                                            onChange={(e) => {
                                              setMonthFormat(e.target.value);
                                              setItemChanged(!itemChanged);
                                              setCurrentItem({
                                                ...currentItem,
                                                monthFormat: e.target.value
                                              });
                                            }}>
                                      <option value="m">M (7)</option>
                                      <option value="mm">MM (07)</option>
                                      <option value="mmm">MMM (LIP)</option>
                                    </select>
                                  </div>
                                </div>

                              </div>

                            </div>

                            <div className="row form-group mb-4">
                              <div className="col-md-3 form-label">
                                <label htmlFor="pattern" className=""></label>
                              </div>
                              <div className="col-md-9">
                                <label htmlFor="pattern" className="">Definicja wzorca <FontAwesomeIcon icon={faCheck}
                                                                                                        className={validPattern ? "valid" : "invalid"}/>
                                </label>
                                <input
                                  type="text"
                                  className="form-control"
                                  id="pattern"
                                  ref={patternRef}
                                  autoComplete="off"
                                  value={currentItem.pattern}
                                  disabled={!currentFormState.formAddingDataMode && currentItem.locked}
                                  required
                                  onChange={(e) => {
                                    setPattern(e.target.value);
                                    setItemChanged(!itemChanged);
                                    setCurrentItem({
                                      ...currentItem,
                                      pattern: e.target.value
                                    });
                                    setCurrentFormState({...currentFormState, formSaveButtonDisabled: false});
                                  }}
                                  onClick={() => resetInvalidInputField("pattern")}
                                />
                                <p id="pattern_note" className={!validPattern ? "instructions" : "offscreen"}>
                                  <FontAwesomeIcon icon={faInfoCircle}/>
                                  Wzorzec musi zawierać jedno oznaczenie N (kolejny numer).<br/>
                                  Opcjonalnie wzorzec może zawierać S (zdefiniowany symbol), M (miesiąc), R (rok)<br/>
                                  Jako separator można użyć spacji, dwukropka, znaku podkreślenia, ukośników. <br/>
                                  Przykładowe wzorce:<br/>
                                  RMN (202405123) <br/>
                                  S/RM/N (XYZ/202505/123)<br/>
                                  N_RM_S (123_202505_XYZ)<br/>
                                  S:R/M N (XYZ:2025/05 123)
                                </p>
                                <p id="pattern_example" className={validPattern ? "" : "offscreen"}>
                                  Przykład: {patternExample}<br/>
                                </p>
                              </div>
                            </div>

                            <div className="row form-group mb-4">
                              <div className="col-md-3 form-label">
                                <label htmlFor="continuousNumbering">Sposób liczenia numerów</label>
                              </div>
                              <div className="col-md-9">
                                <label className="form-check">
                                  <input
                                    id="continuousNumbering"
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={!!currentItem.continuousNumbering}
                                    disabled={!currentFormState.formAddingDataMode && currentItem.locked}
                                    onChange={e => {
                                      setItemChanged(!itemChanged);
                                      setCurrentItem({...currentItem, continuousNumbering: e.target.checked});
                                      setCurrentFormState({...currentFormState, formSaveButtonDisabled: false});
                                    }}
                                  />
                                  <span className="form-check-label">Numeracja ciągła w całym roku</span>
                                </label>
                                <div className="small text-muted mt-1">Wyłączone: numer N zaczyna się od 1 w każdym miesiącu i definicja wzorca musi zawierać M. Włączone: numer rośnie przez cały rok.</div>
                                {!monthlyModeIsValid && <div className="err_info mt-1">Przy numeracji miesięcznej wzorzec musi zawierać oznaczenie M.</div>}
                              </div>
                            </div>

                            {!currentFormState.formAddingDataMode && currentItem.locked && (
                              <div className="alert alert-warning py-2">
                                Ten wzorzec został już użyty do nadania numeru umowie. Definicja numeracji jest zablokowana, aby nie zmieniać historii dokumentów.
                              </div>
                            )}

                            <p id="conflict_notification" className={updateConflict ? "err_info" : "offscreen"}>
                              <FontAwesomeIcon icon={faInfoCircle}/>&nbsp;
                              Nie można zapisać wzorca. Dla tego typu umowy i roku istnieje już inny wzorzec lub definicja jest zablokowana.
                            </p>
                          </form>
      </SettingsEntityModal>

      <DeleteWarningModal state={state} onDelete={onDelete} deleteItemName={itemName}/>
    </div>
  );
}

export default DocumentNumberingPattern;
